import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom'
import Footer from '../../component/Footer';
import Header from '../../component/Header';
import { addToCart } from "../../action";
import './Cartpage.css';
import axios from "../../helper/axios";
import { getCartItemDisplayPrice, repriceCartItem } from "../../helper/discount";

import { useDispatch, useSelector } from "react-redux";

const Cartpage = () => {
    const dispatch = useDispatch();
    const cartData = useSelector(state => state.cart?.cartItems)
    const [qty, setQty] = useState(1);
    const [productPriceData, setProductPriceData] = useState({}); // Store fresh price data for display only

    const cart = useSelector((state) => state.cart);
    const auth = useSelector(state => state?.productData?.users);

    const getProductDetailsForItem = (item) => {
        const productId = item?._id || item?.externalId;
        const loc = item?.location || "";
        return (
            productPriceData[`${productId}::${loc}`] ||
            productPriceData[productId] ||
            null
        );
    };

    // Display only - order totals are recalculated by the API
    const getDisplayPrice = (item) => getCartItemDisplayPrice(item, getProductDetailsForItem(item));

    // Calculate total based on validated display prices (reactive to productPriceData and cartData)
    const validatedTotal = useMemo(() => {
        if (!cartData || cartData.length === 0) return 0;
        
        let total = 0;
        cartData.forEach((item) => {
            const displayPrice = getDisplayPrice(item);
            const itemPrice = displayPrice.hasDiscount2 && displayPrice.sale2 > 0 
                ? displayPrice.sale2 
                : (displayPrice.hasDiscount && displayPrice.sale > 0 
                    ? displayPrice.sale 
                    : displayPrice.regular);
            total += itemPrice * (item.quantity || 1);
        });
        
        return total;
    }, [cartData, productPriceData]);

    // Fetch fresh product data for display purposes only (when component mounts)
    useEffect(() => {
        const fetchFreshPriceData = async () => {
            if (!cartData || cartData.length === 0) return;

            try {
                const priceDataMap = {};
                const promises = cartData.map(async (item) => {
                    try {
                        const productId = item?._id || item?.externalId;
                        const dataId = {
                            _id: productId,
                            location: item?.location || ""
                        };
                        const res = await axios.post("/getproductbyid", dataId);
                        if (res?.data?.length > 0) {
                            const preferred =
                                (item?.location &&
                                    res.data.find(
                                        (row) =>
                                            String(row?.productDetails?.location || row?.location || "") ===
                                            String(item.location)
                                    )) ||
                                res.data[0];
                            const productDetails = preferred?.productDetails;
                            priceDataMap[`${productId}::${item?.location || ""}`] = productDetails;
                            priceDataMap[productId] = productDetails;
                        }
                    } catch (error) {
                        console.error(`Error fetching price data for ${item?._id}:`, error);
                    }
                });

                await Promise.all(promises);
                setProductPriceData(priceDataMap);

                // Re-price cart on refresh using latest DB prices (persisted cart can be stale)
                const updated = (cartData || []).map((it) => {
                    const productId = it?._id || it?.externalId;
                    const productDetails =
                        priceDataMap[`${productId}::${it?.location || ""}`] ||
                        priceDataMap[productId];
                    const p = repriceCartItem(it, productDetails);
                    return { ...it, ...p };
                });

                if (updated.length > 0) {
                    const totalPrice = updated.reduce((acc, it) => {
                        const unit = Number(it?.sale_price || 0) > 0 ? Number(it.sale_price) : Number(it?.regular_price || 0);
                        return acc + unit * Number(it?.quantity || 1);
                    }, 0);
                    updated[0] = { ...updated[0], totalPrice: Number(totalPrice.toFixed(2)) };
                }

                dispatch(addToCart(updated));
            } catch (error) {
                console.error("Error fetching fresh price data:", error);
            }
        };

        fetchFreshPriceData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const onQuantityIncrement = () => {
        setQty(qty + 1);
    };

    const onQuantityDecrement = () => {
        if (qty <= 1) return;
        setQty(qty - 1);
    };

    const remove = async (item) => {
        const sku = item?.item_code;
        const uniqueId = item?.uniqueId;
        const filteredCart = cartData.filter((it) => {
            if (uniqueId && it.uniqueId) return String(it.uniqueId) !== String(uniqueId);
            if (sku) return String(it.item_code) !== String(sku);
            return it._id !== item?._id;
        });
        dispatch(addToCart(filteredCart));
    }

    const updateCartData = (id, type, qty, item_code) => {
        let foundIndex = cartData.findIndex(element => element.item_code === item_code);

        // item_code

        if (qty > 1 || type === "plus") {
            if (type === "minus") {
                cartData[foundIndex].quantity = cartData[foundIndex].quantity - 1;
            } else {
                const stock = cartData[foundIndex].stockQty;
                if (stock > cartData[foundIndex].quantity) {
                    cartData[foundIndex].quantity = cartData[foundIndex].quantity + 1;
                }
            }
            dispatch(addToCart(cartData))
        }
    }


    return (
        <>
            <Header />
            <div className="cart-page cartpage_conatiner sm:container mx-auto mt-20">
                {/* Cart Header */}
                {cartData.length > 0 && (
                    <header className="cart-page__header">
                        <h1 className="cart-page__title">
                            Shporta
                            <svg className="cart-page__title-icon" xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M6 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                <path d="M17 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                <path d="M17 17h-11v-14h-2" />
                                <path d="M6 5l14 1l-1 7h-13" />
                            </svg>
                        </h1>
                    </header>
                )}

                {/* Layout: items left, summary right (desktop) */}
                {cartData.length > 0 && (
                    <div className="cart-page__layout">
                        {/* Cart Items List */}
                        <div className="cart-page__list">
                            {cartData.map((dtt) => (
                                <article key={dtt.uniqueId || dtt.item_code || `${dtt._id}-${dtt.size}`} className="cart-page__item">
                                    <Link to={`/productdetails/${dtt._id}/${dtt.itemCategory}`} className="cart-page__item-image-wrap">
                                        <div className="cart-page__item-image">
                                            <img src={dtt.images[0]} alt={dtt.name} />
                                        </div>
                                    </Link>
                                    <div className="cart-page__item-details">
                                        <div className="cart-page__item-top">
                                            <span className="cart-page__item-vendor">{dtt.vendor}</span>
                                            <button
                                                type="button"
                                                onClick={() => remove(dtt)}
                                                className="cart-page__item-remove"
                                                aria-label="Hiq nga shporta"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M4 4l8 8M12 4l-8 8" />
                                                </svg>
                                            </button>
                                        </div>
                                        <Link to={`/productdetails/${dtt._id}/${dtt.itemCategory}`} className="cart-page__item-name">
                                            {dtt.name?.slice(0, 35)}...
                                        </Link>
                                        {(() => {
                                            const displayPrice = getDisplayPrice(dtt);
                                            return (
                                                <div className="cart-page__item-prices">
                                                    {displayPrice.hasDiscount2 && displayPrice.sale2 > 0 ? (
                                                        <>
                                                            <span className="cart-page__price-old">{displayPrice.regular?.toFixed(2)}€</span>
                                                            {displayPrice.sale > 0 && (
                                                                <span className="cart-page__price-old">{displayPrice.sale?.toFixed(2)}€</span>
                                                            )}
                                                            <span className="cart-page__price-current">{displayPrice.sale2?.toFixed(2)}€</span>
                                                        </>
                                                    ) : displayPrice.hasDiscount && displayPrice.sale > 0 ? (
                                                        <>
                                                            <span className="cart-page__price-old">{displayPrice.regular?.toFixed(2)}€</span>
                                                            <span className="cart-page__price-current">{displayPrice.sale?.toFixed(2)}€</span>
                                                        </>
                                                    ) : (
                                                        <span className="cart-page__price-current">{displayPrice.regular?.toFixed(2)}€</span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <div className="cart-page__item-meta">
                                            <span className="cart-page__item-size">
                                                <strong>Madhësia:</strong> {dtt.size}
                                            </span>
                                            <div className="cart-page__qty">
                                                <button
                                                    type="button"
                                                    onClick={() => updateCartData(dtt._id, "minus", dtt.quantity, dtt.item_code)}
                                                    aria-label="Zbrit sasinë"
                                                >
                                                    −
                                                </button>
                                                <input value={dtt.quantity} readOnly aria-label="Sasia" />
                                                <button
                                                    type="button"
                                                    onClick={() => updateCartData(dtt._id, "plus", dtt.quantity, dtt.item_code)}
                                                    aria-label="Shto sasinë"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>

                        {/* Cart Summary (right column on desktop) */}
                        <aside className="cart-page__summary-column">
                            <div className="cart-page__summary">
                                <div className="cart-page__summary-total">
                                    <span className="cart-page__summary-label">Totali</span>
                                    <span className="cart-page__summary-amount">
                                        {validatedTotal.toFixed(2)} {process.env.REACT_APP_CURRENCY}
                                    </span>
                                    <span className="cart-page__summary-vat">Përfshirë TVSH</span>
                                </div>
                                <Link to="/checkout" className="cart-page__checkout-btn">
                                    Vazhdo me blerjen
                                </Link>
                            </div>
                            <div className="cart-page__shipping">
                            Tarifa e transportit është <strong>3€</strong> për çdo porosi.
                            </div>
                        </aside>
                    </div>
                )}

                {/* Empty Cart View */}
                {cartData.length === 0 && (
                    <div className="cart-page__empty">
                        <h1 className="cart-page__empty-title">Shporta</h1>
                        <p className="cart-page__empty-text">Ju nuk keni ende produkte në shportë.</p>
                        <Link to="/">
                            <button type="button" className="cart-page__empty-btn">Shiko koleksionet</button>
                        </Link>
                    </div>
                )}

                <div className="cart-page__spacer" />
            </div>
            <Footer />
        </>
    )
}

export default Cartpage;
